import React, { FC, useEffect, useState } from 'react';
import type { PropsWithChildren } from 'react';
import { createPortal } from 'react-dom';
import { useMarketContext } from '../../providers/MarketContextProvider';

type MarketElement = HTMLElement & {
  componentOnReady?: () => Promise<unknown>;
};

type DialogEl = MarketElement & {
  dialogID?: string;
};

type ContextEl = MarketElement & {
  open?: (dialog: DialogEl) => Promise<void>;
  close?: (dialogID?: string) => Promise<void>;
};

type ContextManagerEl = MarketElement & {
  activate?: () => Promise<void>;
};

// Lifts our context above the parent <market-modal-full> (which carries tw-z-52).
// Without this, the partial's context renders behind the full's stacking context.
const NESTED_CONTEXT_Z_INDEX = 'tw-z-53';

const hasMarketContextChild = (manager: HTMLElement): boolean => Array.from(manager.children).some((child) => child.tagName === 'MARKET-CONTEXT');

const waitForManagerHasAnyContext = async (manager: HTMLElement, timeoutMs = 2000, intervalMs = 50): Promise<boolean> => {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (hasMarketContextChild(manager)) {
      return true;
    }
    await new Promise((resolve) => {
      setTimeout(resolve, intervalMs);
    });
  }
  return hasMarketContextChild(manager);
};

// Opens children as a stacked Market dialog inside the active <market-context-manager>.
// Each instance creates its own sibling <market-context> so Market treats the dialog
// as a real nested modal (with veil, focus trap, and stacking) rather than queuing it
// behind the parent dialog. Mirrors @market/react's MarketModalPartial overlay flow,
// but reuses the existing page-level context manager from MarketContextProvider.
export const InModal: FC<PropsWithChildren> = ({ children }) => {
  const [contextEl, setContextEl] = useState<ContextEl | null>(null);
  const { getContextManager } = useMarketContext();

  useEffect(() => {
    let cancelled = false;
    const manager = getContextManager() as ContextManagerEl | null;
    if (!manager) return;

    const setup = async () => {
      await manager.componentOnReady?.();
      await waitForManagerHasAnyContext(manager);
      if (cancelled) return;

      const ownContext = document.createElement('market-context') as ContextEl;
      ownContext.classList.add(NESTED_CONTEXT_Z_INDEX);
      manager.appendChild(ownContext);
      if (cancelled) {
        ownContext.remove();
        return;
      }
      setContextEl(ownContext);
    };

    setup().catch((error) => {
      console.error('Failed to set up nested Market context', error);
    });

    return () => {
      cancelled = true;
    };
  }, [getContextManager]);

  useEffect(() => {
    if (!contextEl) return undefined;

    let cancelled = false;
    let openedDialog: DialogEl | null = null;
    let wasDismissedByMarket = false;
    const manager = getContextManager() as ContextManagerEl | null;

    const handleDismissed = () => {
      wasDismissedByMarket = true;
    };

    const open = async () => {
      const dialog = contextEl.firstElementChild as DialogEl | null;
      if (!dialog) return;

      await contextEl.componentOnReady?.();
      await dialog.componentOnReady?.();
      if (cancelled) return;

      dialog.addEventListener('marketDialogDismissed', handleDismissed);

      try {
        await contextEl.open?.(dialog);
        // Capture by reference so cleanup can close the dialog even after React
        // has torn down the portaled child from contextEl.
        openedDialog = dialog;
      } catch (error) {
        console.error('Failed to open nested Market dialog', error);
        return;
      }

      try {
        await manager?.activate?.();
      } catch (error) {
        console.error('Failed to activate Market context manager', error);
      }
    };

    open().catch((error) => {
      console.error('Failed to open nested Market dialog', error);
    });

    return () => {
      cancelled = true;
      openedDialog?.removeEventListener('marketDialogDismissed', handleDismissed);

      const cleanup = async () => {
        if (openedDialog && !wasDismissedByMarket && openedDialog.dialogID && contextEl.close) {
          try {
            await contextEl.close(openedDialog.dialogID);
          } catch (error) {
            console.error('Failed to close nested Market dialog', error);
          }
        }
        if (contextEl.parentElement === manager) {
          contextEl.remove();
        }
      };

      cleanup().catch((error) => {
        console.error('Failed to clean up nested Market context', error);
      });
    };
  }, [contextEl, getContextManager]);

  if (!contextEl) return null;

  return createPortal(children, contextEl);
};
